/**
 * Bubble View State Module
 * Manages all module state, transform state, and view state
 */

import * as Store from "../../store/index.js";
import * as CanvasRenderer from "./canvas-renderer.js";
import * as LabelLOD from "./label-lod.js";
import * as Titles from "./titles.js";
import { isMobileShellVisible } from "../../../../shared/utils/device-detect.js";

// ============================================================================
// CORE STATE
// ============================================================================

let container = null;
let callbacks = null;
let currentState = null;
let currentDocId = null;

// ============================================================================
// DOM REFERENCES
// ============================================================================

let world = null;
let canvas = null;
let dragLayer = null;

// ============================================================================
// VIEW STATE
// ============================================================================

let bState = { zoom: 1, panX: 0, panY: 0 };
let hoveredBubbleId = null;
let retryCount = 0;
let focused = null;
let lastTreeStructureHash = null;
let renderScheduled = false;

// ============================================================================
// CONSTANTS
// ============================================================================

export const MIN_SIZE = 100;
export const PADDING = 60;

// ============================================================================
// ZOOM LIMITS AND FIT
// ============================================================================

// The zoom goes down to 0.01, or lower when the whole map needs it: a map's
// size grows with its items (20,000 items: 143,700 × 113,470 units, shown
// whole at about 0.007), and a fixed 0.01 left it out of reach (2026-10-15).
const ZOOM_FLOOR = 0.01;
export const MAX_ZOOM = 3;
const FIT_MARGIN = 20;
let minZoom = ZOOM_FLOOR;
let fitRequested = false;

export function getMinZoom() {
  return minZoom;
}

export function clampZoom(zoom) {
  return Math.min(MAX_ZOOM, Math.max(minZoom, zoom));
}

/**
 * The view showing bounds whole in a width × height canvas, centered, at
 * most at true size (zoom 1). Also lowers the zoom floor to that zoom, so
 * zooming out always reaches the whole map.
 * @param {{minX:number,minY:number,maxX:number,maxY:number}} bounds - world units
 * @returns {{zoom:number,panX:number,panY:number}}
 */
export function fitTransform(bounds, width, height) {
  const contentW = Math.max(1, bounds.maxX - bounds.minX);
  const contentH = Math.max(1, bounds.maxY - bounds.minY);
  const whole = Math.min((width - FIT_MARGIN * 2) / contentW, (height - FIT_MARGIN * 2) / contentH);
  minZoom = Math.min(ZOOM_FLOOR, whole);
  const zoom = Math.min(1, whole);
  return {
    zoom,
    panX: (width - contentW * zoom) / 2 - bounds.minX * zoom,
    panY: (height - contentH * zoom) / 2 - bounds.minY * zoom,
  };
}

/** Fit once the bubbles are placed (index.js afterLayout); set the view after this call. */
export function requestFit() {
  fitRequested = true;
}

export function hasFitRequest() {
  return fitRequested;
}

/** A fitted view, set without cancelling the fit request (setBState would). */
export function setFittedView(view) {
  bState = { ...bState, ...view };
}

export function takeFitRequest() {
  const requested = fitRequested;
  fitRequested = false;
  return requested;
}

// ============================================================================
// TRANSFORM SAVE DEBOUNCE
// ============================================================================

let transformSaveTimeout = null;
let lastSavedTransform = { zoom: 1, panX: 0, panY: 0 };

// ============================================================================
// CORE STATE GETTERS/SETTERS
// ============================================================================

export function getContainer() {
  return container;
}

export function setContainer(el) {
  container = el;
}

export function getCallbacks() {
  return callbacks;
}

export function setCallbacks(cbs) {
  callbacks = cbs;
}

export function getCurrentState() {
  return currentState;
}

export function setCurrentState(state) {
  currentState = state;
}

export function getCurrentDocId() {
  return currentDocId;
}

export function setCurrentDocId(id) {
  currentDocId = id;
}

// ============================================================================
// DOM REFERENCE GETTERS/SETTERS
// ============================================================================

export function getWorld() {
  return world;
}

export function getCanvas() {
  return canvas;
}

export function getDragLayer() {
  return dragLayer;
}

/**
 * Set all DOM references at once
 * @param {Object} refs - {world, canvas, dragLayer}
 */
export function setDomRefs(refs) {
  world = refs.world;
  canvas = refs.canvas;
  dragLayer = refs.dragLayer;
}

// ============================================================================
// VIEW STATE GETTERS/SETTERS
// ============================================================================

export function getBState() {
  return bState;
}

// A view set before a requested fit happens (a zoom, a pan, a task
// centered, the phone's preview) cancels the fit: it would replace that view
// once the layout is done.
export function setBState(newState) {
  bState = { ...newState };
  fitRequested = false;
}

export function setBStateField(field, value) {
  bState[field] = value;
  fitRequested = false;
}

export function getHoveredBubbleId() {
  return hoveredBubbleId;
}

export function setHoveredBubbleId(id) {
  hoveredBubbleId = id;
}

export function getRetryCount() {
  return retryCount;
}

export function setRetryCount(count) {
  retryCount = count;
}

export function getFocused() {
  return focused;
}

export function setFocused(node) {
  focused = node;
}

export function getLastTreeStructureHash() {
  return lastTreeStructureHash;
}

export function setLastTreeStructureHash(hash) {
  lastTreeStructureHash = hash;
}

export function getRenderScheduled() {
  return renderScheduled;
}

export function setRenderScheduled(scheduled) {
  renderScheduled = scheduled;
}

export function getLastSavedTransform() {
  return lastSavedTransform;
}

export function setLastSavedTransform(transform) {
  lastSavedTransform = { ...transform };
}

// ============================================================================
// TRANSFORM FUNCTIONS
// ============================================================================

/**
 * Update world transform (zoom/pan)
 */
export function updateTransform(options = {}) {
  const transform = `translate(${bState.panX}px, ${bState.panY}px) scale(${bState.zoom})`;
  // Canvas mode reads pan/zoom at paint time — request a repaint on every
  // transform change (pinch, pan, zoom buttons, fit). No-op when unmounted.
  CanvasRenderer.markDirty();
  if (world) {
    world.style.transform = transform;
  }
  if (dragLayer) {
    dragLayer.style.transform = transform;
  }
  // Hide bubbles and labels that are off-screen or too small at this zoom:
  // painting them is what makes large-map pan/zoom stutter. No-op in canvas
  // mode (empty cache).
  LabelLOD.apply(bState.panX, bState.panY, bState.zoom);
  // The titles follow the bubbles' size on screen (titles.js).
  Titles.schedule();
  const zlvl = container?.querySelector("#b-zlvl");
  if (zlvl) {
    // Large maps are seen whole well under 1% (20,000 items: 0.5%).
    const percent = bState.zoom * 100;
    zlvl.textContent = (percent < 10 ? percent.toFixed(1) : Math.round(percent)) + "%";
  }

  // Save to Store (debounced, only if changed). Mobile embedded previews pass
  // save=false so viewing/pinching a preview cannot overwrite the user's saved
  // desktop bubble transform.
  if (options.save !== false) {
    saveTransformToStore();
  }
}

/**
 * Save transform state to Store (debounced, only if changed)
 */
export function saveTransformToStore() {
  // Same guard as the mindmap: mobile preview transforms must never persist
  // as the user's desktop bubble transform (see zoom-pan.js rationale).
  if (isMobileShellVisible()) return;
  if (transformSaveTimeout) {
    clearTimeout(transformSaveTimeout);
  }
  transformSaveTimeout = setTimeout(() => {
    // Only save if values actually changed
    if (
      bState.zoom !== lastSavedTransform.zoom ||
      bState.panX !== lastSavedTransform.panX ||
      bState.panY !== lastSavedTransform.panY
    ) {
      lastSavedTransform = {
        zoom: bState.zoom,
        panX: bState.panX,
        panY: bState.panY,
      };
      Store.setBubbleTransform(lastSavedTransform);
    }
  }, 1000);
}

/**
 * Zoom into a node
 * @param {string} id - Node ID to zoom into
 * @param {Function} onNavigate - Callback to notify navigation
 * @param {Function} renderFn - Function to call to re-render bubbles
 */
export function zoomTo(id, onNavigate, renderFn) {
  currentState = { ...currentState, focusId: id };
  // Entering a bubble shows its content whole (at most at true size): at
  // zoom 1 a large bubble's content was bigger than the screen.
  bState.zoom = 1;
  bState.panX = 0;
  bState.panY = 0;
  fitRequested = true;

  if (onNavigate) {
    onNavigate(id);
  }

  setTimeout(() => {
    if (renderFn) renderFn();
  }, 0);
}

// ============================================================================
// SCHEDULING FUNCTIONS
// ============================================================================

/**
 * Schedule a render for the next animation frame (batches multiple requests)
 *
 * @param {boolean} forceImmediate - If true, interrupts physics and renders immediately
 *                                   (used for structural changes like add/delete/move)
 * @param {Function} renderFn - Function to call to render bubbles
 * @param {Object} physicsState - {isRunning, cancelFn, setRenderPending}
 */
export function scheduleRender(forceImmediate, renderFn, physicsState) {
  // If structural change (forceImmediate), interrupt physics and render now
  if (forceImmediate && physicsState?.isRunning()) {
    // Cancel current physics - it will restart after render with new data
    if (physicsState.cancelFn) {
      physicsState.cancelFn();
    }
  }

  // If physics is running and NOT a structural change, defer the render
  if (physicsState?.isRunning() && !forceImmediate) {
    if (physicsState.setRenderPending) {
      physicsState.setRenderPending(true);
    }
    return;
  }

  if (renderScheduled) return;
  renderScheduled = true;

  requestAnimationFrame(() => {
    renderScheduled = false;
    if (container && callbacks && currentState && renderFn) {
      renderFn();
    }
  });
}

// ============================================================================
// SELECTION FUNCTIONS
// ============================================================================

/**
 * Update visual selection state without re-rendering
 * @param {string} selectedId - ID of the selected node
 */
export function updateSelectionVisual(selectedId) {
  if (!world) return;

  // Remove selection from all bubbles
  world.querySelectorAll(".bubble.selected").forEach((b) => {
    b.classList.remove("selected");
  });

  // Add selection to the selected bubble
  if (selectedId) {
    const selectedBubble = world.querySelector(
      `.bubble[data-id="${selectedId}"]`,
    );
    if (selectedBubble) {
      selectedBubble.classList.add("selected");
    }
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Compute a hash of tree structure (node ids and children structure)
 * Used to detect actual structural changes vs property-only updates
 * Returns a string that changes only when nodes are added/removed/moved
 * Includes collapsed state since it affects visible structure
 * Has depth guard to prevent stack overflow on very deep trees
 *
 * @param {Object} node - Root node to hash
 * @param {boolean} hideCompleted - Whether completed nodes are hidden
 * @param {number} depth - Current recursion depth (internal)
 * @returns {string} Hash string
 */
export function computeTreeStructureHash(node, hideCompleted = false, depth = 0) {
  if (!node) return "";

  // Guard against very deep trees causing stack overflow
  if (depth > 1000) {
    console.warn('[BubbleView] computeTreeStructureHash: max depth exceeded');
    return '';
  }

  // Skip completed nodes if hideCompleted is true (check both legacy status and Done tag)
  if (hideCompleted && node.status === "done") return "";
  // Note: tag-based Done check can't run here (no callbacks in static function).
  // The bubble view's update() detects tag changes via tagFilterChanged and forces re-render.

  // Hash this node's id, collapsed state, and tags (all affect visible structure)
  let hash = node.id;
  // Include collapsed state in hash since it determines
  // whether children are rendered (visible structure change)
  if (node.collapsed) {
    hash += ":C";
  }
  // Include tags so changes trigger re-render
  if (node.tags?.length) {
    hash += ":T" + node.tags.map(t => typeof t === 'string' ? t : t.tagId).join(",");
  }

  // Only recurse into children if not collapsed (they're hidden when collapsed)
  if (node.children && node.children.length > 0 && !node.collapsed) {
    hash += "[";
    for (const child of node.children) {
      const childHash = computeTreeStructureHash(child, hideCompleted, depth + 1);
      if (childHash) hash += childHash + ",";
    }
    hash += "]";
  }
  return hash;
}

/**
 * Find a node by ID in a tree
 * @param {Object} node - Root node to search
 * @param {string} id - ID to find
 * @returns {Object|null} Found node or null
 */
export function findNodeById(node, id) {
  if (!node) return null;
  if (node.id === id) return node;
  if (node.children) {
    for (const child of node.children) {
      const found = findNodeById(child, id);
      if (found) return found;
    }
  }
  return null;
}

// ============================================================================
// CLEANUP
// ============================================================================

/**
 * Reset all state to initial values
 */
export function resetState() {
  container = null;
  callbacks = null;
  currentState = null;
  currentDocId = null;
  world = null;
  canvas = null;
  dragLayer = null;
  bState = { zoom: 1, panX: 0, panY: 0 };
  hoveredBubbleId = null;
  retryCount = 0;
  focused = null;
  lastTreeStructureHash = null;
  renderScheduled = false;
  lastSavedTransform = { zoom: 1, panX: 0, panY: 0 };
  minZoom = ZOOM_FLOOR;
  fitRequested = false;
  if (transformSaveTimeout) {
    clearTimeout(transformSaveTimeout);
    transformSaveTimeout = null;
  }
}
